import _ from 'underscore';
import { useEffect, useMemo, useContext, useCallback } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useAccountLevelVariables, usePostponeReadVariables } from '@bingads-webui-campaign-react/basic-context-utils';
import { useAccount } from '@bingads-webui/overreact-cc';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PaymentContext } from '@bingads-webui-cc-react/payment-context';
import { usePromotionBannerContext } from '@bingads-webui-campaign-react/promotion-banner';
import { getUrlWithProtocol } from '@bingads-webui-campaign-react/url-utils';

import { usePromotionalOffer } from './use-promotional-offer';
import { useCouponBannerInfo } from './use-coupon-banner-info';
import { DEFAULT_ACTIVITY } from '../helpers';

export function useInitializeWizardPromotionalOffer({
  activity = DEFAULT_ACTIVITY,
  backupCoupon,
  website,
  isSignUp,
  isSkipWebsiteValidation,
  getCouponCurrencyCode = _.noop,
}) {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const { scope } = useContext(ScopeContext);
  const { paymentContext, setPaymentContext } = useContext(PaymentContext);
  const { bannerInfo, setBannerInfo } = usePromotionBannerContext();

  const {
    CurrentAccount: currentAccount,
  } = serverContext;

  const accountLevelVariables = useAccountLevelVariables();
  const postponeReadConfig = usePostponeReadVariables();
  const [{ data: account }, { read: readAccount }]
    = useAccount(accountLevelVariables, postponeReadConfig);

  useEffect(() => {
    if (!account && !isSignUp) {
      readAccount();
    }
  }, [account, isSignUp, readAccount]);

  const [{
    coupon,
    dialogCoupon,
  }, {
    initializePromotionalOffers,
    updateUserCoupon,
    resetCouponStore,
  }] = usePromotionalOffer({
    activity,
    backupCoupon,
    website,
    isSignUp,
    isSkipWebsiteValidation,
  });

  const canEvaluatePromotionOffers = useMemo(() => (
    // website could be changed due to append missing protocol after url validation
    // prepend it before hand to avoid trigger init twice
    isSkipWebsiteValidation || getUrlWithProtocol(website)
  ), [isSkipWebsiteValidation, website]);

  const couponBannerInfo = useCouponBannerInfo({ coupon, i18n, getCouponCurrencyCode });

  useEffect(() => {
    if ((!couponBannerInfo && !bannerInfo) ||
      (couponBannerInfo && bannerInfo && _.isEqual(couponBannerInfo, bannerInfo))) {
      return;
    }
    setBannerInfo(couponBannerInfo);
  }, [bannerInfo, couponBannerInfo, setBannerInfo]);

  useEffect(() => {
    if (canEvaluatePromotionOffers && ((isSignUp && currentAccount) ||
      (!isSignUp && account && !account.PreferredBillToPaymentInstrumentId))) {
      initializePromotionalOffers();
    }
  }, [initializePromotionalOffers, account, canEvaluatePromotionOffers, isSignUp, currentAccount]);

  const updateCoupon = useCallback(({
    couponString,
    couponStringType,
    isManual,
    updatedCouponCallback,
    isSetUrlCoupon,
  }) => {
    let couponCode = null;
    let couponClass = null;
    scope.remove(['cco', 'ccl']);

    if (isSetUrlCoupon && couponString) {
      const couponOffer = {};
      couponOffer[couponStringType] = couponString;
      scope.append(couponOffer);

      if (couponStringType === 'cco') {
        couponCode = couponString;
      } else {
        couponClass = couponString;
      }
    }

    setPaymentContext({
      ...paymentContext,
      ccl: couponClass,
      cco: couponCode,
    });
    scope.save();

    updateUserCoupon({
      couponString,
      couponStringType,
      isManualCouponCode: isManual,
      updatedCouponCallback,
    });
  }, [
    updateUserCoupon,
    scope,
    setPaymentContext,
    paymentContext]);

  return [{
    coupon,
    dialogCoupon,
  }, {
    updateCoupon,
    resetCouponStore,
  }];
}
